import * as d3 from 'd3-selection'
import * as colors from 'colors'

// Function to calculate the average width of labels
function averageWidth (selection) {
  let width = 0
  let count = 0

  selection.each(function () {
    const label = d3.select(this).select('.value-label')
    const bbox = label.node()?.getBBox() || { width: 0 }
    width += bbox.width
    count++
  })

  return width / count
}

const valueLabels = (selection, outerRadius, pointOffset, serieColor, width) => {
  selection.each(function () {
    d3.select(this)
      .selectAll('text.value-label')
      .data(d => d.formatted?.value ? [d] : [])
      .join('text')
        .text(d => d.formatted.value)
        .attr('class', 'value-label')
        .attr('dy', (d, i, all) => {
          // Above the point
          return (-outerRadius * 2) - pointOffset
        })
        .style('fill', d => colors.adjustLightness(colors.lookup(serieColor), -20))
  })
  // Now prune the labels if they overlap
  const avg = averageWidth(selection)
  const desiredCount = Math.floor(width / avg)
  const interval = Math.ceil(selection.size() / desiredCount)

  selection.each(function (d, i) {
    d3.select(this)
      .select('.value-label')
      .style('opacity', i % interval === 0 ? 1 : 0)
  })
}

const nameLabels = (selection, outerRadius, pointOffset, serieColor, maxWidth) => {
  // Grab just the last point in the selection
  selection.filter((d, i) => i === selection.size() - 1)
    .each(function () {
      d3.select(this)
        .selectAll('g.text-group')
        .data(d => d.formatted?.title ? [d] : [])
        .join('g')
          .attr('class', 'text-group')
          .attr('transform', `translate(${outerRadius * 2}, 0)`)
          .selectAll('text.name-label')
          .data(d => [d])
          .join('text')
          .text(d => d.formatted.title)
          .attr('class', 'name-label')
          .style('fill', d => colors.adjustLightness(colors.lookup(serieColor), -20))
          .call(function (selection) {
            // truncrate the text
            selection.each(function () {
              let content = d3.select(this).text()
              while (Math.floor(this.getBBox().width) > maxWidth && content.length > 0) {
                content = content.slice(0, -1)
                d3.select(this).text(content + '...')
              }
            })
          })
    })

    // Make sure any points that aren't the last are not showing the name label
    selection.filter((d, i) => i !== selection.size() - 1)
      .each(function () {
        d3.select(this)
          .select('.text-group')
          .remove()
      })

}

const circles = (selection, multi, size, serieColor) => {
  selection.each(function (d, i) {
    d3.select(this)
      .selectAll('circle')
      .data(d => !multi ? [d, d] : [d])
      .join('circle')
      .attr('r', (d, i) => {
        if (i > 0) {
          // it's the inner smaller circle
          return size.singleSerieInnerRadius
        } else {
          return multi ? size.multiSerieRadius : size.singleSerieOuterRadius
        }
      })
      .attr('class', (d, i) => {
        return i > 0 ? 'inner' : ''
      })
      .filter((d, i) => i == 0)
      .style('fill', d => {
        // outer circle's get the point color if defined. Otherwise the serie color
        return colors.lookup(d.color || serieColor)
      })
  })
}

export default function (scalers0, scalers1, size, chart, areaPoint, points) {
  const draw = function (selection) {
    selection.each(function (data) {
      let group = d3.select(this)
        , series = chart.datum().plots[0].series
        , multi = areaPoint ? true : chart.layout.nonAnalyticsSeries(series).length > 1
        , outerRadius = multi ? size.multiSerieRadius : size.singleSerieOuterRadius
        , pointOffset = multi ? 4 : 0 // Increase the distance between the point and the label if we have multiple series

      points = (points || data.points).filter(d => {
        return Number.isFinite(d._index) ||
              d && !isNaN(d.raw)
      })

      group.classed('has-value-labels', points.some(p => p.formatted?.value))

      let pointsByRangeId = points.map(p => p.rangeId)
        , point = group.selectAll('.point:not(.head)') // Add the circle points. Each data point circle is going to be in a group
                       .data(points.map(p => Object.assign({}, p)), d => d.rangeId) // Clone each point so we can easily grab previous values during transitions

      point.exit().remove()

      let update = point.enter()
                        .append('g')
                          .attr('class', 'point')
                          .attr('transform', function (d, i) {
                            return `translate(${scalers0.x(chart.xAccessor.apply(this, arguments))}, ${scalers0.y(d._range[1], d)})`
                          })
                          .merge(point)
                          .attr('class', 'point ' + (!multi ? 'hollow-circle' : ''))
                          .classed('force-point', function (d, i) {
                            if (pointsByRangeId.length === 1) {
                              // Only one point, force it.
                              return true
                            }
                            let idx = data.points.map(p => p.rangeId).indexOf(d.rangeId) // Idx of this point in the real dataset
                              , prev = data.points[idx - 1]
                              , next = data.points[idx + 1]

                            if (i === 0) {
                              // First point we're drawning, force a point if the next point isn't drawn
                              return next && pointsByRangeId.indexOf(next.rangeId) !== 1
                            }

                            if (i === pointsByRangeId.length - 1) {
                              // Last point, force a point if the previous point isn't drawn
                              return prev && pointsByRangeId.indexOf(prev.rangeId) !== i - 1
                            }

                            // Finally, we aren't showing the next or prev point, force a draw
                            return (prev && next) &&
                                   pointsByRangeId.indexOf(prev.rangeId) === -1 &&
                                   pointsByRangeId.indexOf(next.rangeId) === -1
                          })

      update.call(circles, multi, size, data.color)
        .call(nameLabels, outerRadius, pointOffset, data.color, chart.layout.rightMarginOffset)
        .call(valueLabels, outerRadius, pointOffset, data.color, chart._scalers.x.groupScale.range().at(-1))

      let transform = function (d, i) {
        return `translate(${scalers1.x(chart.xAccessor.apply(this, arguments))}, ${scalers1.y(d._range[1], d)})`
      }

      try {
        // TODO Let's inherit the transition on the selection instead of this chart.transition crap
        update.transition(chart.defaultTransition)
              .attr('transform', transform)
      } catch (e) {
        // The transition no longer exists, just do a basic update. We won't need
        // this try/catch once we fix the aforementioned TODO
        update.attr('transform', transform)
      }

    })
  }

  return draw
}
